// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
}

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

interface StaffAssignment {
  event_id: string;
  permissions: string[];
}

type PermissionKey = 'check_in' | 'view_registrations' | 'edit_registrations' | 'manage_event' | 'view_reports' | 'all';

const PERMISSIONS: { key: PermissionKey; label: string; description: string }[] = [
  { key: 'check_in', label: 'Check-in attendees', description: 'Scan QR codes and mark registrations as checked in' },
  { key: 'view_registrations', label: 'View registrations', description: 'See registration list and attendee details' },
  { key: 'edit_registrations', label: 'Edit registrations', description: 'Modify registration details and contact info' },
  { key: 'manage_event', label: 'Manage event settings', description: 'Edit event details, pricing, and schedule' },
  { key: 'view_reports', label: 'View reports', description: 'Access analytics, revenue, and attendance reports' },
  { key: 'all', label: 'Full access', description: 'All permissions above' },
];

interface AddEditStaffModalProps {
  isOpen: boolean;
  onClose: (shouldRefresh: boolean) => void;
  staffMember: StaffMember | null;
  organizationId: string;
}

const AddEditStaffModal: React.FC<AddEditStaffModalProps> = ({
  isOpen,
  onClose,
  staffMember,
  organizationId,
}) => {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'active',
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<PermissionKey[]>(['check_in']);
  const [existingAssignments, setExistingAssignments] = useState<StaffAssignment[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  // Fetch upcoming events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!organizationId || !isOpen) return;
      
      setIsLoadingEvents(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const { data, error } = await supabase
          .from('events')
          .select('id, name, start_date, end_date')
          .eq('organization_id', organizationId)
          .gte('end_date', today)
          .order('start_date', { ascending: true });

        if (error) throw error;
        setEvents(data || []);
      } catch (error) {
        console.error('Error fetching events:', error);
        toast.error('Failed to load events');
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
  }, [organizationId, isOpen]);

  // Load staff data and assignments
  useEffect(() => {
    const loadStaffData = async () => {
      if (staffMember) {
        setFormData({
          name: staffMember.name,
          email: staffMember.email,
          phone: staffMember.phone || '',
          status: staffMember.status,
        });

        // Fetch existing assignments
        try {
          const { data, error } = await supabase
            .from('event_staff_assignments')
            .select('event_id, permissions')
            .eq('staff_id', staffMember.id);

          if (error) throw error;
          
          setExistingAssignments(data || []);
          setSelectedEvents(data?.map(a => a.event_id) || []);
          
          // Get all unique permissions from assignments
          const allPerms = new Set<PermissionKey>();
          data?.forEach(a => a.permissions.forEach(p => allPerms.add(p as PermissionKey)));
          setPermissions(Array.from(allPerms));
        } catch (error) {
          console.error('Error loading assignments:', error);
        }
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          status: 'active',
        });
        setSelectedEvents([]);
        setPermissions(['check_in']);
        setExistingAssignments([]);
      }
    };

    loadStaffData();
  }, [staffMember]);

  const handleEventToggle = (eventId: string) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleSelectAllEvents = () => {
    setSelectedEvents(events.map(e => e.id));
  };

  const handleDeselectAllEvents = () => {
    setSelectedEvents([]);
  };

  const handlePermissionToggle = (perm: PermissionKey) => {
    if (perm === 'all') {
      if (permissions.includes('all')) {
        // Uncheck 'all' - leave others as-is
        setPermissions(prev => prev.filter(p => p !== 'all'));
      } else {
        // Check 'all' - auto-check all others
        setPermissions(PERMISSIONS.map(p => p.key));
      }
    } else {
      setPermissions(prev =>
        prev.includes(perm)
          ? prev.filter(p => p !== perm)
          : [...prev, perm]
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!organizationId) {
      toast.error('Organization ID is missing');
      return;
    }

    if (selectedEvents.length === 0) {
      toast.error('Please assign at least one event');
      return;
    }

    if (permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      setIsSaving(true);

      // Check if email already exists (for new staff or email change)
      if (!staffMember || formData.email !== staffMember.email) {
        console.log('Checking for duplicate email:', formData.email, 'in org:', organizationId);
        
        const { data: existingStaff, error: checkError } = await supabase
          .from('event_staff')
          .select('id, email, name')
          .eq('email', formData.email)
          .eq('organization_id', organizationId)
          .maybeSingle();

        console.log('Duplicate check result:', { existingStaff, checkError });

        if (checkError) {
          console.error('Error checking for duplicates:', checkError);
        }

        if (existingStaff) {
          console.log('Found existing staff member:', existingStaff);
          toast.error('A staff member with this email already exists');
          setIsSaving(false);
          return;
        }
      }

      let staffId = staffMember?.id;

      if (staffMember) {
        // Update existing staff
        const { error } = await supabase
          .from('event_staff')
          .update({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            status: formData.status,
          })
          .eq('id', staffMember.id);

        if (error) throw error;

        // Delete existing assignments
        const { error: deleteError } = await supabase
          .from('event_staff_assignments')
          .delete()
          .eq('staff_id', staffMember.id);

        if (deleteError) throw deleteError;
      } else {
        // Create new staff
        const { data, error } = await supabase
          .from('event_staff')
          .insert({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            status: formData.status,
            organization_id: organizationId,
            created_by: profile?.id,
          })
          .select()
          .single();

        if (error) throw error;
        staffId = data.id;

        // Send staff invitation email to create user account
        if (!staffMember) { // Only for new staff members
          console.log('Sending staff invitation for:', data.id);
          try {
            const { data: inviteData, error: inviteError } = await invokeEdgeFunction(
              'invite-staff-member',
              {
                body: {
                  staff_id: data.id,
                  email: formData.email,
                  name: formData.name,
                  organization_id: organizationId,
                },
              }
            );

            if (inviteError) {
              console.error('Error sending staff invitation:', inviteError);
              toast.warning('Staff member added but invitation email failed to send');
            } else {
              console.log('Staff invitation sent:', inviteData);
            }
          } catch (inviteErr) {
            console.error('Exception sending invitation:', inviteErr);
            toast.warning('Staff member added but invitation email failed');
          }
        }
      }

      // Insert new assignments
      const assignments = selectedEvents.map(eventId => ({
        staff_id: staffId,
        event_id: eventId,
        permissions: permissions,
      }));

      const { error: assignError } = await supabase
        .from('event_staff_assignments')
        .insert(assignments);

      if (assignError) throw assignError;

      toast.success(staffMember ? 'Staff member updated successfully' : 'Staff member added successfully');
      onClose(true);
    } catch (error: any) {
      console.error('Error saving staff:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Full error:', JSON.stringify(error, null, 2));
      
      // Handle duplicate email error
      if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
        toast.error('A staff member with this email already exists');
      } else {
        toast.error(error.message || 'Failed to save staff member');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {staffMember ? 'Edit Staff Member' : 'Add Staff Member'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* Section 1: Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Basic Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter staff member name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="flex items-center justify-between space-x-2">
                  <div className="space-y-0.5">
                    <Label htmlFor="status">Active Status</Label>
                    <p className="text-sm text-muted-foreground">
                      Active staff can access assigned events
                    </p>
                  </div>
                  <Switch
                    id="status"
                    checked={formData.status === 'active'}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, status: checked ? 'active' : 'inactive' })
                    }
                  />
                </div>
              </div>

              {/* Section 2: Event Assignments */}
              <div className="space-y-4 border-t pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Assign to Events</h3>
                  <p className="text-sm text-muted-foreground">Select which events this staff member can access</p>
                </div>

                {isLoadingEvents ? (
                  <div className="text-sm text-muted-foreground">Loading events...</div>
                ) : events.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No upcoming events available</div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllEvents}
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleDeselectAllEvents}
                      >
                        Deselect All
                      </Button>
                    </div>

                    <div className="space-y-2 border rounded-lg p-3 max-h-[200px] overflow-y-auto">
                      {events.map((event) => (
                        <div key={event.id} className="flex items-start space-x-2">
                          <Checkbox
                            id={`event-${event.id}`}
                            checked={selectedEvents.includes(event.id)}
                            onCheckedChange={() => handleEventToggle(event.id)}
                          />
                          <label
                            htmlFor={`event-${event.id}`}
                            className="text-sm flex-1 cursor-pointer"
                          >
                            <div className="font-medium">{event.name}</div>
                            <div className="text-muted-foreground text-xs">
                              {format(parseLocalDate(event.start_date), 'MMM d, yyyy')} - {format(parseLocalDate(event.end_date), 'MMM d, yyyy')}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Section 3: Permissions */}
              <div className="space-y-4 border-t pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Permissions</h3>
                  <p className="text-sm text-muted-foreground">What can this staff member do at assigned events?</p>
                </div>

                <div className="space-y-3">
                  {PERMISSIONS.map((perm) => (
                    <div key={perm.key} className="flex items-start space-x-3">
                      <Checkbox
                        id={`perm-${perm.key}`}
                        checked={permissions.includes(perm.key)}
                        onCheckedChange={() => handlePermissionToggle(perm.key)}
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={`perm-${perm.key}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {perm.label}
                        </label>
                        <p className="text-xs text-muted-foreground">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Saving...' : staffMember ? 'Save Changes' : 'Add Staff'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditStaffModal;
